import React, {useEffect, useRef, useState} from "react"
import {v4 as uuid} from "uuid"
import {isEmpty} from "lodash"
import {useTranslation} from "react-i18next"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableProps, Order} from "types/common"
import {handleError, checkPermission, toastError, toastWarning, scrollTableToBottom, toastSuccess} from "helpers"
import {settingAcademicTrackService} from "services"
import {KlassappTableHOC} from "HOC"
import {BaseDepartmentId} from "types/departments"
import {PermissionsRequired} from "components/PermissionsRequired"
import {Permissions, PermissionUserType} from "types/permission"
import {getPermissionsByDepartment} from "sections/Settings/helper"
import {useModel} from "hooks"
import styles from "./AcademicTrack.module.css"
import {Icon} from "components"
import {Button, Checkbox} from "antd"
import {AcademicTrackPhasesAndShifts} from "../AcademicTrackPhasesAndShifts"
import moment from "moment"
import {ResyncPhaseAndShiftPopup} from "./ResyncPhaseAndShiftPopup"
import {Filter} from "./Filter"
import {useDebounce} from "react-use"

type Props = KlassappTableProps

const AcademicTrackTab = (props: Props) => {
    const {t} = useTranslation(["academics", "common"])
    const model = useModel()

    const [newItemData, setNewItemData] = useState<any>(null)
    const [oldItemData, setOldItemData] = useState<any>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [permissions, setPermissions] = useState<PermissionUserType>({staff: []})
    const [selectedAcademicTrack, setSelectedAcademicTrack] = useState<any>(null)
    const [searchValue, setSearchValue] = useState("")
    const [currentFilter, setCurrentFilter] = useState<{isActive: boolean | null}>({isActive: null})
    const [searchDebounce, setSearchDebounce] = useState("")
    const [resyncPopup, setResyncPopup] = useState({
        isVisible: false,
        academicTrackIds: [],
        campuses: [],
        programs: [],
        programVersions: [],
        startDate: null
    })

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        fields,
        allFields,
        menuActions,
        isHideMenuActions,
        isShowTableHeaderAction,
        tableHeaderActions,
        isLoading,
        orderField,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onClickShowConfirmModal,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        tableRef,
        getCurrentData
    } = props

    // Store latest data-fetching function in a ref so the one-time dispatchFunc registration
    // always calls the latest version
    const getDataRef = useRef<(...args: any[]) => any>(() => {})

    // --- Render helpers ---

    const renderNameHtml = (item: any) => {
        return (
            <div className={styles.titleWrap}>
                {item.isLocked ? (
                    <div className={styles.lockIcon}>
                        <Icon icon="LOCK_CHECKMARK" />
                    </div>
                ) : (
                    ""
                )}
                <span className={styles.title}>{item.name}</span>
            </div>
        )
    }

    const renderIsActiveHtml = (disabled = true, value?: boolean) => {
        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox checked={value} />
                    </div>
                </div>
            )
        }
        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={!!newItemData?.isActiveData}
                        onChange={(event) => onChangeNewItemData("isActiveData", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    const renderViewPhasesAndShiftsBtn = (item: any) => {
        return <BaseButton title={"View Phases And Shifts"} onClick={() => setSelectedAcademicTrack(item)} />
    }

    const renderResyncPhasesAndShiftsBtn = (item: any) => {
        return (
            <BaseButton
                variant={"secondary"}
                disabled={!item.isActive}
                title={"Resync Phases And Shifts"}
                onClick={() => openResyncPopup([item.trackId])}
            />
        )
    }

    // --- Data fetching ---

    const getParams = (searchOverride?: string) => {
        const search = searchOverride !== undefined ? searchOverride : searchDebounce
        const params: any = {
            range: {
                page,
                pageSize
            },
            filter: {}
        }
        if (search) {
            params.filter.search = search
        }
        if (currentFilter.isActive !== null) {
            params.filter.isActive = currentFilter.isActive
        }
        params.sort = {
            orderBy: "isActive",
            orderDir: Order.Desc
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        return params
    }

    const getData = async (searchOverride?: string) => {
        dispatch({isLoading: true})
        const params = getParams(searchOverride)
        try {
            const {data: apiData, total: apiTotal} = await settingAcademicTrackService.getAllAcademicTracks(params)
            const newData = apiData.map((item) => ({
                ...item,
                id: item.trackId,
                nameHtml: renderNameHtml(item),
                isActiveHtml: renderIsActiveHtml(true, !!item.isActive),
                viewPhasesAndShiftsBtn: renderViewPhasesAndShiftsBtn(item),
                resyncPhasesAndShiftsBtn: renderResyncPhasesAndShiftsBtn(item)
            }))
            dispatch({data: newData, total: apiTotal})
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    // Keep getDataRef up to date
    getDataRef.current = getData

    // --- Permissions ---

    const currentPermissions = permissions
    const currentModel = model

    const getPermissionsAndSetup = () => {
        const perms = getPermissionsByDepartment(BaseDepartmentId.Academics)
        setPermissions(perms)
    }

    // --- Resync popup ---

    const openResyncPopup = (academicTrackIds: number[]) => {
        setResyncPopup({
            isVisible: true,
            academicTrackIds,
            campuses: [],
            programs: [],
            programVersions: [],
            startDate: null
        })
    }

    const closeResyncPopup = () => {
        setResyncPopup((prev) => ({...prev, isVisible: false}))
    }

    const onChangeResyncPopup = (key: string, value: any) => {
        setResyncPopup((prev) => {
            const popup = {...prev, [key]: value}
            if (key === "campuses") {
                popup.programs = []
                popup.programVersions = []
            }
            if (key === "programs") {
                popup.programVersions = []
            }
            return popup
        })
    }

    const onConfirmResyncPopup = async () => {
        const campusIds = (resyncPopup.campuses || []).map((item: any) => item.id).filter(Boolean)
        const programIds = (resyncPopup.programs || []).map((item: any) => item.id).filter(Boolean)
        const programVersionIds = (resyncPopup.programVersions || []).map((item: any) => item.id).filter(Boolean)

        if (!campusIds.length) {
            toastError("Campus is required.")
            return
        }

        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.resyncAcademicTrackPhasesAndShifts({
                academicTrackIds: resyncPopup.academicTrackIds,
                campusIds,
                programIds,
                programVersionIds,
                startDate: resyncPopup.startDate ? moment(resyncPopup.startDate).format("YYYY-MM-DD") : undefined
            })
            closeResyncPopup()
            toastSuccess("Sync process started successfully and will take a few moments to complete.")
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    // --- CRUD operations ---

    const onChangeNewItemData = (key: string, value: any) => {
        setNewItemData((prev) => ({...prev, [key]: value}))
    }

    const renderNameInput = () => {
        return (
            <BaseInput
                value={newItemData?.nameData}
                placeholder="Name"
                onChange={(value) => onChangeNewItemData("nameData", value)}
            />
        )
    }

    const renderDescriptionInput = () => {
        return (
            <BaseInput
                value={newItemData?.descriptionData}
                placeholder="Description"
                onChange={(value) => onChangeNewItemData("descriptionData", value)}
            />
        )
    }

    const onClickAddItem = () => {
        const newItem: any = {
            id: uuid(),
            nameData: "",
            descriptionData: "",
            isActiveData: false,
            isForm: true
        }
        const currentData = getCurrentData?.() || data
        currentData.push(newItem)
        setNewItemData(newItem)
        setOldItemData(null)
        dispatch({data: currentData})
        scrollTableToBottom(tableRef)
    }

    const validateDataBeforeSubmit = () => {
        const {nameData, descriptionData} = newItemData || {}
        if (!nameData) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.academicTrack")
                })
            )
            return false
        }
        if (!descriptionData) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.description")
                })
            )
            return false
        }
        return true
    }

    const createActivity = async (submitData: any) => {
        try {
            dispatch({isLoading: true})
            setIsSubmitting(true)
            await settingAcademicTrackService.createAcademicTrack(submitData)
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
            setIsSubmitting(false)
        }
    }

    const updateActivity = async (id: number, submitData: any) => {
        try {
            dispatch({isLoading: true})
            setIsSubmitting(true)
            await settingAcademicTrackService.updateAcademicTrack(id, submitData)
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
            setIsSubmitting(false)
        }
    }

    const onClickSave = async () => {
        if (!validateDataBeforeSubmit()) {
            return
        }
        const {nameData, descriptionData, isForm, id, isActiveData} = newItemData || {}
        if (isForm) {
            await createActivity({
                name: nameData,
                description: descriptionData,
                isActive: !!isActiveData
            })
        } else {
            await updateActivity(id, {
                name: nameData,
                description: descriptionData,
                isActive: !!isActiveData
            })
        }
        setNewItemData(null)
        setOldItemData(null)
        getDataRef.current?.()
    }

    const onClickCancel = () => {
        const currentData = getCurrentData?.() || data
        const newData = currentData
            .filter((item: any) => !item.isForm)
            .map((item: any) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = oldItemData.name
                    item.description = oldItemData.description
                    item.isActiveHtml = renderIsActiveHtml(true, oldItemData.isActive)
                    return item
                }
                return item
            })
        setNewItemData(null)
        setOldItemData(null)
        dispatch({data: newData})
    }

    const onClickEdit = (editItem: any) => {
        if (newItemData) {
            toastWarning(t("common:validation.saveBeforeEdit"))
            return
        }
        setNewItemData({
            ...editItem,
            nameData: editItem.name,
            descriptionData: editItem.description,
            isActiveData: editItem.isActive,
            isEdit: true
        })
        setOldItemData({...editItem})
    }

    const onClickDelete = async (deletedItem: any) => {
        if (deletedItem.isForm) {
            setNewItemData(null)
            setOldItemData(null)
            getDataRef.current?.()
            return
        }
        try {
            dispatch({isLoading: true})
            setNewItemData(null)
            setOldItemData(null)
            await settingAcademicTrackService.deleteAcademicTrack([deletedItem.id])
            getDataRef.current?.()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickDeleteMulti = async () => {
        const currentData = getCurrentData?.() || data
        const checkedItems = currentData.filter((item: any) => item.isChecked)
        const payload = checkedItems.map((item: any) => item.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.deleteAcademicTrack(payload)
            getDataRef.current?.()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickDuplicateMulti = async () => {
        const currentData = getCurrentData?.() || data
        const payload = currentData
            .filter((item: any) => item.isChecked)
            .map(({name, description}: any) => ({
                name: `${name} (Copy)`,
                description
            }))
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.duplicateAcademicTrack(payload)
            getDataRef.current?.()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    // --- Filter handlers ---

    const changeSearch = (value: string) => {
        setSearchValue(value)
    }

    const onChangeFilter = (key: string, value: any) => {
        setCurrentFilter((prev) => ({...prev, [key]: value}))
    }

    const applyFilter = () => {
        getDataRef.current?.()
    }

    const clearFilter = () => {
        setCurrentFilter({isActive: null})
        setSearchValue("")
        // Fetch immediately with empty search and default filter
        const params: any = {
            range: {page, pageSize},
            filter: {},
            sort: {
                orderBy: "isActive",
                orderDir: Order.Desc
            }
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        dispatch({isLoading: true})
        settingAcademicTrackService
            .getAllAcademicTracks(params)
            .then(({data: apiData, total: apiTotal}) => {
                const newData = apiData.map((item) => ({
                    ...item,
                    id: item.trackId,
                    nameHtml: renderNameHtml(item),
                    isActiveHtml: renderIsActiveHtml(true, !!item.isActive),
                    viewPhasesAndShiftsBtn: renderViewPhasesAndShiftsBtn(item),
                    resyncPhasesAndShiftsBtn: renderResyncPhasesAndShiftsBtn(item)
                }))
                dispatch({data: newData, total: apiTotal})
            })
            .catch((e) => handleError(e))
            .finally(() => dispatch({isLoading: false}))
    }

    // --- Table config callbacks ---

    const getPageTitle = () => t("settings.academicTrack")

    const getFields = () => [
        t("settings.academicTrack"),
        t("settings.description"),
        t("settings.isActive"),
        "View Phases And Shifts",
        "Resync Phases And Shifts"
    ]

    const getColumns = () => [
        {
            title: t("settings.academicTrack"),
            field: "nameHtml",
            style: {minWidth: "200px"},
            sortable: true,
            orderField: "name"
        },
        {
            title: t("settings.description"),
            field: "description",
            style: {minWidth: "200px"},
            sortable: true
        },
        {
            title: t("settings.isActive"),
            field: "isActiveHtml",
            style: {minWidth: "80px"},
            sortable: true,
            orderField: "isActive"
        },
        {
            title: "View Phases And Shifts",
            field: "viewPhasesAndShiftsBtn"
        },
        {
            title: "Resync Phases And Shifts",
            field: "resyncPhasesAndShiftsBtn"
        }
    ]

    const getTableHeaderActions = (isShowDuplicateBtn = true, checkedData = []) => {
        const canWrite = checkPermission(currentPermissions, currentModel)
        if (!canWrite) {
            return []
        }
        const hasLockedItem = (checkedData || []).some((item: any) => item.isLocked)
        const actions: any[] = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal?.("DELETE")
            })
        }
        if (isShowDuplicateBtn) {
            actions.push({
                title: t("common:action.duplicate"),
                icon: "DUPLICATE",
                action: () => onClickDuplicateMulti()
            })
        }
        return actions
    }

    const getMenuActions = () => [
        {
            title: t("common:action.edit"),
            icon: "EDIT",
            action: onClickEdit,
            canShow: () => checkPermission(currentPermissions, currentModel)
        },
        {
            title: t("common:action.delete"),
            icon: "DELETE",
            action: onClickDelete,
            canShow: (data: any) => checkPermission(currentPermissions, currentModel) && !data.isLocked
        }
    ]

    // --- Effects ---

    useEffect(() => {
        if (!newItemData) {
            return
        }
        const currentData = getCurrentData?.() || data
        const newData = currentData.map((item: any) => {
            const isInputItem = item.id === newItemData.id
            if (!isInputItem) {
                return item
            }
            return {
                ...item,
                nameHtml: renderNameInput(),
                description: renderDescriptionInput(),
                isActiveHtml: renderIsActiveHtml(false)
            }
        })
        dispatch({data: newData})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newItemData])

    // Initial setup — runs once on mount
    useEffect(() => {
        getPermissionsAndSetup()
        dispatchFunc?.([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getDataRef.current},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "onClickDuplicateMulti", func: onClickDuplicateMulti},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete}
        ])
        getDataRef.current?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Auto-fetch when debounced search changes (skip first render)
    const isFirstRender = useRef(true)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        getDataRef.current?.()
    }, [searchDebounce])

    // --- Render ---

    const renderResyncPopup = () => {
        const selectedAcademicTrackNames =
            getCurrentData?.()
                ?.filter((item: any) => resyncPopup.academicTrackIds.includes(item.trackId))
                .map((item: any) => item.name) || []

        return (
            <ResyncPhaseAndShiftPopup
                isVisible={resyncPopup.isVisible}
                isSubmitting={isSubmitting}
                resyncPopup={resyncPopup}
                selectedAcademicTrackNames={selectedAcademicTrackNames}
                onClose={closeResyncPopup}
                onChange={onChangeResyncPopup}
                onConfirm={onConfirmResyncPopup}
            />
        )
    }

    return (
        <div className={styles.wrapper}>
            {selectedAcademicTrack ? (
                <div className={styles.phaseAndShiftsTemplate}>
                    <div className={styles.backHeader}>
                        <Button
                            onClick={() => setSelectedAcademicTrack(null)}
                            icon={<Icon icon="BACK" color="#FF349B" />}
                            className={styles.backBtnIcon}
                        />
                        <span className={styles.backTitle}>{selectedAcademicTrack.name}</span>
                    </div>
                    <AcademicTrackPhasesAndShifts academicTrack={selectedAcademicTrack} />
                </div>
            ) : (
                <>
                    <Filter
                        searchValue={searchValue}
                        changeSearch={changeSearch}
                        onApplyFilter={applyFilter}
                        clearFilter={clearFilter}
                        currentFilter={currentFilter}
                        onChangeFilter={onChangeFilter}
                    />
                    <KlassappTableHeader
                        isShowAction={isShowTableHeaderAction}
                        actions={tableHeaderActions}
                        page={page}
                        total={total}
                        defaultPageSize={pageSize}
                        onChangePage={onChangePage}
                        onChangeRowPerPage={onChangeRowPerPage}
                        fields={fields}
                        allFields={allFields}
                        onChangeFields={onChangeFields}
                        onChangeAllFields={onChangeAllFields}
                        onDraggableColumn={onDraggableColumn}
                    />
                    <KlassappTable
                        columns={columns}
                        data={data}
                        menuActions={isHideMenuActions ? [] : menuActions}
                        isLoading={isLoading}
                        fields={fields}
                        allFields={allFields}
                        orderField={orderField}
                        isShowCheckedColumn
                        tableRef={tableRef}
                        keyRender="trackId"
                        onClickRowItem={() => {}}
                        onChangeFields={onChangeFields}
                        onUpdateRowData={onUpdateRowData}
                        onUpdateTableData={onUpdateTableData}
                        onClickSortColumn={onClickSortColumn}
                        onDraggableColumn={onDraggableColumn}
                        onChangeAllFields={onChangeAllFields}
                    />
                    <PermissionsRequired permissions={{staff: [Permissions.Staff.Settings.Modules.Academics.Edit]}}>
                        {!newItemData ? (
                            !isLoading && <AddItemCircleButton onClick={onClickAddItem} />
                        ) : (
                            <div className={styles.buttonWrap}>
                                <SecondaryButton
                                    title={t("common:action.cancel")}
                                    onClick={onClickCancel}
                                    className={styles.cancelBtn}
                                />
                                <BaseButton
                                    title={t("common:action.save").toUpperCase()}
                                    onClick={onClickSave}
                                    loading={isSubmitting}
                                />
                            </div>
                        )}
                    </PermissionsRequired>
                    {renderResyncPopup()}
                </>
            )}
        </div>
    )
}

export const AcademicTrack = KlassappTableHOC(AcademicTrackTab)
